'use client';

import type { ReactNode } from 'react';
import { Alert, Box, ScrollArea, Stack, Text } from '@mantine/core';

import { CONTENT_LOCALES, LOCALE_LABELS } from '../lib/content/constants';
import { portalStrings } from '../lib/content/ui-strings';
import { ECHOENGINE } from '../lib/portal/product';

/**
 * The branded failure surface, shared by every Echo Engine error boundary.
 *
 * Shown in both locales rather than the reader's chosen one: the failure may be
 * the very thing that stopped the locale from resolving, and a reader who cannot
 * read the recovery instructions is stuck.
 *
 * The error message is never rendered — a server-side failure can carry
 * infrastructure detail. The digest is safe to show and is what correlates with
 * the server log.
 */
export function EchoengineErrorNotice({ digest, action }: { digest?: string; action?: ReactNode }) {
  const primary = portalStrings(CONTENT_LOCALES[0], ECHOENGINE);

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Box p="lg" maw={640}>
          <Alert variant="light" color="red" radius="md" title={primary.errorTitle} role="alert">
            <Stack gap="sm">
              {CONTENT_LOCALES.map((locale) => (
                <Text key={locale} size="sm" lang={LOCALE_LABELS[locale].languageTag}>
                  {portalStrings(locale, ECHOENGINE).errorHelp}
                </Text>
              ))}

              {digest ? (
                <Text size="xs" c="dimmed" ff="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace">
                  {digest}
                </Text>
              ) : null}

              {action}
            </Stack>
          </Alert>
        </Box>
      </ScrollArea>
    </div>
  );
}
